import { cva, VariantProps } from "class-variance-authority";
import { useContext, useEffect, useMemo } from "react";
import { Checkbox } from "../../checkbox";
import { MenuSelect } from "../../menu-select";
import { MenuSelectItemProps } from "../../menu-select/menu-select-item";
import { DrawerSelectGroupContext } from "../drawer-select-group";
import { DrawerSelectContext } from "../drawer-select.context";
import { DrawerSelectOptionType } from "../drawer-select.types";
import styles from "./drawer-select-option.module.css";

const optionCva = cva([styles.Option]);

export type DrawerSelectOptionProps = MenuSelectItemProps &
  VariantProps<typeof optionCva> & {
    value: string | number;
    text: string;
  };

export function DrawerSelectOption({
  value,
  text,
  children,
  className,
  ...props
}: DrawerSelectOptionProps) {
  const groupContext = useContext(DrawerSelectGroupContext);
  const context = useContext(DrawerSelectContext);
  const option: DrawerSelectOptionType = useMemo(
    () => ({ value, text }),
    [value, text],
  );

  const displayCheckbox = groupContext.displayCheckbox;

  const isSelected = context.isOptionSelected(option);
  const selectedCount = context.value?.length ?? 0;
  const atMaxOptions =
    context.multiple &&
    context.maxOptions !== undefined &&
    selectedCount >= context.maxOptions;
  const isDisabled = atMaxOptions && !isSelected;
  const showRadioButton = displayCheckbox && !context.multiple;
  const showCheckbox = displayCheckbox && context.multiple;

  const handleClick = () => {
    if (isDisabled) {
      return;
    }

    if (context.multiple) {
      return context.toggleOption(option);
    }

    if (context.variant === "modal") {
      return context.selectOnlyOption(option);
    }

    return context.selectSingleOption(option);
  };

  useEffect(() => {
    groupContext.pushOption(option);
    context.pushOption(option);

    return () => {
      groupContext.removeOption(option);
      context.removeOption(option);
    };
  }, [
    context.pushOption,
    context.removeOption,
    groupContext.pushOption,
    groupContext.removeOption,
    option,
  ]);

  if (!groupContext.isVisible(option)) {
    return null;
  }

  return (
    <MenuSelect.Item
      onClick={handleClick}
      className={optionCva({ className })}
      data-no-gutters={showRadioButton}
      aria-disabled={isDisabled}
      {...props}
    >
      {showRadioButton && (
        <span className={styles.Radio} role="img" aria-hidden>
          <span
            className={styles.RadioCircle}
            data-checked={isSelected}
            aria-hidden
          />
          <span className={styles.RadioLabel}>{children}</span>
        </span>
      )}
      {showCheckbox && (
        <Checkbox
          checked={isSelected}
          disabled={groupContext.disableCheckbox(option)}
          readOnly={true}
        />
      )}
      {!showRadioButton && children}
    </MenuSelect.Item>
  );
}
